import React, { useRef } from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    FormControl,
    FormLabel,
    Input,
    HStack,
    useToast,
    IconButton,
    Spinner,
} from "@chakra-ui/react";
import { FaFilePdf, FaUpload, FaDownload } from "react-icons/fa";

function toInputDate(dateStr) {
    if (!dateStr) return "";
    // Se già in formato YYYY-MM-DD, restituisci così com'è
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    // Se in formato DD/MM/YYYY, converti
    const parts = dateStr.split("/");
    if (parts.length === 3) {
        return `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
    }
    return dateStr;
}

const CorsoModal = ({
    isOpen,
    onClose,
    corso,
    onSave,
    onPdfDownload,
    onPdfUpload,
}) => {
    const [form, setForm] = React.useState({
        nome: corso?.nome || "",
        data: corso?.data || "",
        scadenza: corso?.scadenza || "",
        ente: corso?.ente || "",
        ore: corso?.ore || "",
    });
    const [pdfFile, setPdfFile] = React.useState(null);
    const [uploading, setUploading] = React.useState(false);
    const fileInputRef = useRef();
    const toast = useToast();

    React.useEffect(() => {
        if (!isOpen) return;

        setForm({
            nome: corso?.nome || "",
            data: corso?.data || "",
            scadenza: corso?.scadenza || "",
            ente: corso?.ente || "",
            ore: corso?.ore || "",
        });

        setPdfFile(null);
    }, [corso, isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setPdfFile(e.target.files[0]);
            // Upload immediato e scorrelato dal salvataggio dati corso
            // await uploadPdf(e.target.files[0]);
        }
    };

    const uploadPdf = async (file) => {
        console.log("Uploading PDF:", file.name);
        console.log("Corso ID:", corso?.id);
        if (!file || !corso?.id) return;

        console.log("Inizio upload PDF:", file.name);
        setUploading(true);
        const formData = new FormData();
        formData.append("pdf_certificato", file);
        try {
            // Sostituisci l'URL con quello del tuo backend
            const res = await fetch(`/api/dipendenti/corsi/${corso.id}/certificate`, {
                method: "POST",
                body: formData,
            });
            if (res.ok) {
                console.log("PDF uploaded successfully:", file.name);
                toast({
                    title: "PDF caricato con successo.",
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                });
                if (onPdfUpload) onPdfUpload();
            } else {
                console.error("Error uploading PDF:", res.statusText);
                toast({
                    title: "Errore durante l'upload del PDF.",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (err) {
            console.error("Network error uploading PDF:", err);
            toast({
                title: "Errore di rete durante l'upload.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
        setUploading(false);
    };

    const handleSave = (e) => {
        e.preventDefault();
        onSave({ ...form, pdfFile });
        toast({
            title: "Certificazione aggiornata.",
            status: "success",
            duration: 2000,
            isClosable: true,
        });
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <ModalOverlay />
            <ModalContent as="form" onSubmit={handleSave}>
                <ModalHeader>Modifica Certificazione</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <FormControl mb={3} isRequired>
                        <FormLabel>Nome</FormLabel>
                        <Input
                            name="nome"
                            value={form.nome}
                            onChange={handleChange}
                            required
                        />
                    </FormControl>
                    <HStack spacing={3} mb={3}>
                        <FormControl isRequired>
                            <FormLabel>Data di rilascio</FormLabel>
                            <Input
                                type="date"
                                name="data"
                                value={toInputDate(form.data)}
                                onChange={handleChange}
                                required
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Scadenza</FormLabel>
                            <Input
                                type="date"
                                name="scadenza"
                                value={toInputDate(form.scadenza)}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </HStack>
                    <HStack spacing={3} mb={3}>
                        <FormControl>
                            <FormLabel>Ente</FormLabel>
                            <Input
                                name="ente"
                                value={form.ente}
                                onChange={handleChange}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Ore</FormLabel>
                            <Input
                                type="number"
                                name="ore"
                                value={form.ore}
                                onChange={handleChange}
                                min={1}
                            />
                        </FormControl>
                    </HStack>
                    <FormControl>
                        <FormLabel>Certificato PDF</FormLabel>
                        <HStack>
                            <IconButton
                                icon={<FaDownload />}
                                colorScheme="red"
                                aria-label="Scarica PDF"
                                isDisabled={!corso?.pdf_certificato}
                                onClick={() => onPdfDownload && onPdfDownload(corso)}
                                title="Scarica certificato"
                            />
                            <input
                                type="file"
                                accept="application/pdf"
                                style={{ display: "none" }}
                                ref={fileInputRef}
                                onChange={handleFileChange}
                            />
                            <Button
                                leftIcon={uploading ? <Spinner size="sm" /> : <FaUpload />}
                                onClick={() => fileInputRef.current.click()}
                                variant="outline"
                                maxW="200px"
                                overflow="hidden"
                                textOverflow="ellipsis"
                                whiteSpace="nowrap"
                                title={pdfFile ? pdfFile.name : ""}
                                isDisabled={uploading}
                            >
                                {pdfFile
                                    ? pdfFile.name.length > 25
                                        ? pdfFile.name.slice(0, 22) + "..."
                                        : pdfFile.name
                                    : "Seleziona PDF"}
                            </Button>
                            <Button
                                colorScheme="teal"
                                variant="solid"
                                isLoading={uploading}
                                isDisabled={!pdfFile || uploading}
                                onClick={async () => {
                                    if (pdfFile) {
                                        console.log("Uploading PDF:", pdfFile.name);
                                        await uploadPdf(pdfFile);
                                        setPdfFile(null);
                                        if (fileInputRef.current) fileInputRef.current.value = "";
                                    }else{
                                        console.warn("Nessun file PDF selezionato per l'upload.");
                                    }
                                }}
                            >
                                Carica PDF
                            </Button>
                        </HStack>
                    </FormControl>
                </ModalBody>
                <ModalFooter>
                    <Button colorScheme="teal" mr={3} type="submit">
                        Salva
                    </Button>
                    <Button variant="ghost" onClick={onClose}>
                        Annulla
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default CorsoModal;